/**
 * Main menu functionality for responsive behaviors and dropdowns.
 *
 * @package  Embark
 * @since    1.0.0
 */

/**
 * Import dependencies.
 */
import hoverintent from 'hoverintent';
import {
	disableScroll, enableScroll, isElementInViewport, enableFocusTrap, disableFocusTrap, slideUp, slideDown,
} from './helpers';

const { els, breakpoints } = embark;
const {
	html,
	mainMenu,
	navicon,
	navContainer,
} = els;

/**
 * Check if the site should be displaying the mobile menu or not.
 */
export function isMobileMenu() {
	return breakpoints.menu.belowOrTouch();
}

/**
 * Check if the site should be displaying the desktop menu or not.
 */
export function isDesktopMenu() {
	return !isMobileMenu();
}

export class ResponsiveMenu {
	hoverIntentTimeout = 300;

	isMobile = isMobileMenu();

	constructor({
		menu, navicon, container, menuOpenNaviconClass, menuOpenHtmlClass, menuItemTriggerClass,
	}) {
		if (menu && navicon && container && menuOpenNaviconClass && menuOpenHtmlClass && menuItemTriggerClass) {
			// The menu element.
			this.menu = menu;
			// The button that open's and closes the menu on mobile/touch devices.
			this.navicon = navicon;
			// The element that hidden/shown when the navicon is clicked.
			this.container = container;
			// THe class to add to the navicon element when the mobile menu is open.
			this.menuOpenNaviconClass = menuOpenNaviconClass;
			// The class to add to the html element when the mobile menu is open.
			this.menuOpenHtmlClass = menuOpenHtmlClass;
			// The class of the element that, when clicked, opens its sibling .sub-menu element.
			this.menuItemTriggerClass = menuItemTriggerClass;
			// The CSS selector for sub-menus.
			this.subMenuSelector = '.sub-menu';

			this.init();
		}
	}

	/**
	 * Set some initial menu item classes and all events.
	 */
	init = () => {
		this.subMenus = this.menu.querySelectorAll(this.subMenuSelector);

		// Add event listeners to menu items that have children.
		this.parentItems = document.querySelectorAll('.menu-item-has-children');
		if (this.parentItems.length) {
			this.parentItems.forEach((item) => {
				this.initItemHoverIntent(item);
				this.initItemTriggers(item);
				this.initItemKeyboardEvents(item);
			});
		}

		if (isMobileMenu()) {
			this.initMobileMenu();
		}

		// Add class to the first .current-menu-ancestor to avoid multiple being highlighted.
		this.ancestorItems = document.querySelectorAll('.main-menu > .current-menu-ancestor');
		if (this.ancestorItems.length > 0) {
			this.ancestorItems[0].classList.add('current-menu-ancestor--primary');
		}

		// ✅ Add this event listener to close the mobile menu when clicking a hash link
		this.menu.addEventListener('click', this.handleHashLinkClick);

		window.addEventListener('resize', this.handleResize);
		document.addEventListener('click', this.handleMenuState);
	};

	initItemHoverIntent = (item) => {
		// Add hoverintent delay to menu items to give the user a chance to move their mouse.
		hoverintent(item, () => {
			if (isDesktopMenu()) {
				this.openSubmenu(item);
			}
		}, () => {
			if (isDesktopMenu()) {
				this.closeSubmenu(item);
			}
		}).options({
			timeout: this.hoverIntentTimeout,
		});
	};

	initItemTriggers = (item) => {
		const triggers = item.querySelectorAll(':scope > .menu-item__trigger, :scope > a[href="#"]');
		if (triggers.length) {
			triggers.forEach((trigger) => {
				trigger.addEventListener('click', () => {
					if (isDesktopMenu()) {
						this.parentItems.forEach((_item) => {
							if (_item !== item) {
								this.closeSubmenu(_item);
							} else {
								this.toggleSubmenu(_item);
							}
						});
					} else {
						this.toggleSubmenu(item);
					}
				});
			});
		}
	};

	initItemKeyboardEvents = (item) => {
		item.addEventListener('keydown', (e) => {
			switch (e.key) {
				case 'Escape':
					this.closeSubmenu(item);
					break;

				default:
					break;
			}
		});
	};

	/**
	 * Open all submenus that are marked as current.
	 */
	initMobileMenu = () => {
		// Close all open submenus so they have the base inline styles they need.
		if (this.subMenus.length) {
			this.subMenus.forEach((submenu) => {
				slideUp(submenu);
			});
		}

		// Open all submenus that are marked as current.
		const currentItems = document.querySelectorAll('.menu-item[class*="current"]');
		if (currentItems.length) {
			currentItems.forEach((item) => {
				this.openSubmenu(item);
			});
		}
	};

	/**
	 * Close all open submenus.
	 */
	initDesktopMenu = () => {
		// Close the mobile menu.
		this.closeMobileMenu();

		// Close all open submenus.
		const openItems = this.menu.querySelectorAll('.menu-item.open');
		if (openItems) {
			openItems.forEach((item) => {
				this.closeSubmenu(item);
			});
		}

		// Remove inline styles from submenus.
		if (this.subMenus.length) {
			this.subMenus.forEach((submenu) => {
				submenu.removeAttribute('style');
			});
		}
	};

	/**
	 * Update mobile menu top offset and close opened submenus depending on screensize.
	 */
	handleResize = () => {
		window.requestAnimationFrame(() => {
			if (isDesktopMenu()) {
				if (this.isMobile) {
					this.initDesktopMenu();
					this.isMobile = false;
				}
			} else if (!this.isMobile) {
				this.initMobileMenu();
				this.isMobile = true;
			}
		});
	};

	/**
	 * Open the mobile menu.
	 */
	openMobileMenu = () => {
		disableScroll();
		html.classList.add(this.menuOpenHtmlClass);
		this.navicon.classList.add(this.menuOpenNaviconClass);
		enableFocusTrap(this.container);
	};

	/**
	 * Close the mobile menu.
	 */
	closeMobileMenu = () => {
		// Make sure there isn't a lightbox open before we enable scrolling.
		if (!html.classList.contains('lightbox-open')) {
			enableScroll();
			disableFocusTrap();
		}

		html.classList.remove(this.menuOpenHtmlClass);
		this.navicon.classList.remove(this.menuOpenNaviconClass);
	};

	/**
	 * Toggle the mobile menu.
	 */
	toggleMobileMenu = () => {
		if (html.classList.contains(this.menuOpenHtmlClass)) {
			this.closeMobileMenu();
		} else {
			this.openMobileMenu();
		}
	};

	/**
	 * Close the mobile menu when you click off of it.
	 *
	 * @param  {Event} e
	 */
	handleMenuState = (e) => {
		const { target } = e;
		const isTargetInNavigation = target.closest('.site-header__navigation');

		// If the menu is open, and the event target is the nav or a child of the nav, exit quickly.
		if (html.classList.contains(this.menuOpenHtmlClass) && (target === this.container || isTargetInNavigation)) {
			return;
		}

		// If event target is the navicon or any of its children, trigger the toggle method.
		if (target === this.navicon || target.closest('#js-navicon')) {
			this.toggleMobileMenu();
		} else {
			// Else, this is not the navicon trigger. Close the menu. User has clicked away.
			this.closeMobileMenu();
		}
	};

	/**
	 * Close mobile menu when clicking a hash link.
	 *
	 * @param {Event} event
	 */
	handleHashLinkClick = (event) => {
		const link = event.target.closest('a'); // Find the closest <a> tag

		if (link && link.hash) { // Ensure the link has a hash (#)
			this.closeMobileMenu();
		}
	};

	/**
	 * Get the relevant menu item elements.
	 *
	 * @param {element} item .menu-item element.
	 * @return {object} Object of relevant menu item elements.
	 * @return {element} Object.submenu The submenu element.
	 * @return {element} Object.link The menu item link element.
	 * @return {element} Object.trigger The menu item trigger element.
	 */
	getItemElements = (item) => {
		const elements = {
			submenu: item.querySelector(':scope > .sub-menu'),
			link: item.querySelector(':scope > a'),
			trigger: item.querySelector(':scope > .menu-item__trigger'),
		};

		return elements;
	};

	/**
	 * Open the submenu.
	 *
	 * @param {element} item .menu-item element.
	 * @param {object|boolean} elements Object of relevant menu item elements or false to get them from the item.
	 */
	openSubmenu = (item, elements = false) => {
		const { submenu, link, trigger } = elements || this.getItemElements(item);
		if (submenu && link && trigger) {
			item.classList.add('open');
			link.setAttribute('aria-expanded', 'true');
			trigger.setAttribute('aria-expanded', 'true');
			submenu.setAttribute('aria-hidden', 'false');

			if (isMobileMenu()) {
				slideDown(submenu);
			} else if (submenu && !isElementInViewport(submenu)) {
				item.classList.add('has-offscreen-sub-menu');
			}
		}
	};

	/**
	 * Close the submenu.
	 *
	 * @param {element} item .menu-item element.
	 * @param {object|boolean} elements Object of relevant menu item elements or false to get them from the item.
	 */
	closeSubmenu = (item, elements = false) => {
		const { submenu, link, trigger } = elements || this.getItemElements(item);
		if (submenu && link && trigger) {
			item.classList.remove('open');
			link.setAttribute('aria-expanded', 'false');
			trigger.setAttribute('aria-expanded', 'false');
			submenu.setAttribute('aria-hidden', 'true');

			if (isMobileMenu()) {
				slideUp(submenu);
			}
		}
	};

	/**
	 * Check if the submenu is open or closed and toggle it.
	 *
	 * @param {element} item .menu-item element.
	 */
	toggleSubmenu = (item) => {
		const elements = this.getItemElements(item);
		const { submenu, link, trigger } = elements;

		if (submenu && link && trigger) {
			if (trigger.getAttribute('aria-expanded') === 'false') {
				this.openSubmenu(item, elements);
			} else {
				this.closeSubmenu(item, elements);
			}
		}
	};
}

embark.components.mainMenu = new ResponsiveMenu({
	menu: mainMenu,
	navicon,
	container: navContainer,
	menuOpenNaviconClass: 'navicon--open',
	menuOpenHtmlClass: 'html--menu-open',
	menuItemTriggerClass: 'menu-item__trigger',
});
